<script setup lang="ts">
  import {
    computed,
    nextTick,
    onActivated,
    onBeforeUnmount,
    onMounted,
    reactive,
    ref,
    watch,
  } from 'vue';
  import {
    actions as actionTypes,
    useUserComponent,
    useHelpers,
    createComponentScope,
    Resource,
    uniqueID,
  } from '@integro/core';
  import type { IAppHelpers } from '@integro/core';
  import * as _ from 'lodash';

  defineOptions({
    name: 'UcMeterCharts',
  });

  const componentMetadata = {
    kind: 'usercontrol',
  };

  const emit = defineEmits(['reload', 'close', 'save']);

  const props = defineProps({
    meter: {
      type: Object,
      required: true,
    },
  });

  const {
    $app,
    $fn,
    $i18n,
    $store,
    $config,
    $imodal,
    $notification,
    $tabs,
    $route,
    $router,
    gettext,
    pgettext,
    ngettext,
    npgettext,
    schemaDefaults,
    getSchema,
    searchDataSource,
    randomElementId,
    datetime,
    date,
    time,
    number,
    money,
    distance,
    username,
    filesize,
  } = useHelpers();

  const mapResponseData = (...args) => {
    const data = args[0];
    if (!data.series.values.length) {
      return;
    }

    let columns = data.series.columns,
      tableData = _.map(data.series.values, (p) =>
        _.extend({ id: p[1] }, _.zipObject(columns, p))
      );

    tableData = _.orderBy(tableData, ['time']);

    const times = _.map(tableData, (p) => p.time),
      raw = _.map(tableData, (p) => p.raw),
      values = _.map(tableData, (p) => p.value),
      offsets = _.map(tableData, (p) => p.offset),
      diffs = _.map(tableData, (p) => p.difference);

    return {
      tableData: _.orderBy(tableData, ['time'], ['desc']),
      heatmapData: tableData,
      readings: [times, raw, values],
      consumptions: [times, diffs],
    };
  };

  const stats = ref(null);
  const ux = ref([]);
  const b_5731695935601903_options = computed(() => ({
    title: $fn.value.pgettext('MeterCharts', 'Values'),
    id: 'meter-readings',
    class: 'my-chart',
    width: 500,
    height: 300,
    series: [
      {
        label: $fn.value.pgettext('MeterCharts', 'Time'),
      },
      {
        label:
          $fn.value.pgettext('MeterCharts', 'Raw') +
          ' (' +
          props.meter.unit +
          ')',
        stroke: 'blue',
        fill: '#00437a22',
      },
      {
        label:
          $fn.value.pgettext('MeterCharts', 'Calculated') +
          ' (' +
          props.meter.unit +
          ')',
        stroke: 'magenta',
        fill: '#00437a22',
      },
    ],
  }));
  const b_2272469684735879_options = computed(() => ({
    title: $fn.value.pgettext('MeterCharts', 'Consumption'),
    id: 'consumptions',
    class: 'my-chart',
    width: 500,
    height: 300,
    series: [
      {
        label: $fn.value.pgettext('MeterCharts', 'Time'),
      },
      {
        label: props.meter.unit,
        stroke: '#00437a',
        fill: '#00437a22',
        stepped: {
          align: -1,
        },
      },
    ],
  }));
  const b_4186377094414664_editable = computed(() => false);
  const b_4186377094414664_columns = computed(() => [
    {
      name: 'day',
      header: $fn.value.pgettext('MeterCharts', 'Date'),
      width: '120px',
      formatter: {
        name: 'template',
        value:
          '<span\n\
  class="text-nowrap"\n\
  :class="{\'text-red-500\': $fn.isWeekend(value)}"\n\
>\n\
  {{date(value) }}\n\
</span>\n\
',
      },
    },
    {
      name: 'raw',
      header: $fn.value.pgettext('MeterCharts', 'Raw Value'),
      type: 'number',
      width: '100px',
      textAlign: 'right',
      cellClass: 'text-muted',
      formatter: {
        name: 'template',
        value: '<span>{{number(value) }} {{ row.unit }}</span>',
      },
    },
    {
      name: 'offset',
      header: $fn.value.pgettext('MeterCharts', 'Offset'),
      type: 'number',
      width: '100px',
      cellClass: 'text-muted',
    },
    {
      name: 'value',
      header: $fn.value.pgettext('MeterCharts', 'Offset Value'),
      width: '120px',
      textAlign: 'right',
      cellClass: 'fw-bolder',
      formatter: {
        name: 'template',
        value: '<span>{{number(value) }} {{ row.unit }}</span>',
      },
    },
    {
      name: 'difference',
      header: $fn.value.pgettext('MeterCharts', 'Consumption'),
      width: '120px',
      textAlign: 'right',
      cellClass: 'fw-bolder',
      formatter: {
        name: 'template',
        value: '<span>{{number(value) }} {{ row.unit }}</span>',
      },
    },
  ]);

  const $data = computed(() => ({
    stats: stats.value,
    ux: ux.value,
    b_5731695935601903_options: b_5731695935601903_options.value,
    b_2272469684735879_options: b_2272469684735879_options.value,
    b_4186377094414664_editable: b_4186377094414664_editable.value,
    b_4186377094414664_columns: b_4186377094414664_columns.value,
  }));

  const $$load_stats = async () => {
    return $fn.value
      .fetch('meters/get_daily_stats', {
        parameters: {
          serial: props.meter.serial_number,
          time_from: '2020-01-01',
        },
      })
      .then((d) => mapResponseData(d));
  };

  const {
    loading,
    error,
    loaded,
    reloading,
    showLoadingIndicator,
    loadDataThrottled,
    friendlyName,
    loadData,
    touchLastLoaded,
    handleError,
    setData,
    forms,
    registerForm,
    unregisterForm,
    canLoadData,
    isModalComponent,
    modalName,
    pageDocument: _pageDocument,
    routeTab,
    ux: _ux,
    componentMetadata: _componentMetadata,
    $uid,
    lastLoaded,
  } = useUserComponent(props, {
    metadata: componentMetadata,
    dataSpecs: [
      { name: 'stats' },
      { name: 'ux' },
      { name: 'b_5731695935601903_options', nodeId: '5731695935601903' },
      { name: 'b_2272469684735879_options', nodeId: '2272469684735879' },
      { name: 'b_4186377094414664_editable', nodeId: '4186377094414664' },
      { name: 'b_4186377094414664_columns', nodeId: '4186377094414664' },
    ],
    loaders: { stats: $$load_stats },
    ux: {},
    dataRefs: {
      stats,
      ux,
      b_5731695935601903_options,
      b_2272469684735879_options,
      b_4186377094414664_editable,
      b_4186377094414664_columns,
    },
  });

  const $component = createComponentScope(
    {
      $app,
      $fn,
      $i18n,
      $store,
      $imodal,
      $notification,
      $tabs,
      $router,
    } as IAppHelpers,
    {
      emit,
      forms,
      error,
      isModalComponent,
      modalName,
      componentMetadata,
      loadData,
      registerForm,
      unregisterForm,
      setData,
      touchLastLoaded,
      handleError,
    }
  );

  watch(
    () => props.meter,
    (newValue, oldValue) => {
      if (_.isEqual(newValue, oldValue)) return;
      loadDataThrottled('meter');
    }
  );

  const act_7315092382398562_reloadComponentData_l0044we4e =
    new actionTypes.ReloadComponentDataAction({});
  const inst_evh_7315092382398562_reload_oz244hgox =
    new actionTypes.EventHandlerImpl({
      action: act_7315092382398562_reloadComponentData_l0044we4e,
      event: 'reload',
      displayName: 'reloadComponentData',
    });
  const inst_evh_7315092382398562_reload = new actionTypes.EventHandlerGroup({
    handlers: [inst_evh_7315092382398562_reload_oz244hgox],
    event: 'reload',
  });

  const actions = {
    inst_evh_7315092382398562_reload_oz244hgox,
    inst_evh_7315092382398562_reload,
  };

  const evh_7315092382398562_reload_oz244hgox = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_reload_oz244hgox.executeFromDOM(
      $component,
      event,
      scope
    );
  };
  const evh_7315092382398562_reload = async (event, scope) => {
    return await actions.inst_evh_7315092382398562_reload.executeFromDOM(
      $component,
      event,
      scope
    );
  };

  defineExpose({
    loadData,
    loading,
    loaded,
    error,
  });
</script>

<template>
  <ItpUsercontrol
    :loading="loading"
    :reloading="reloading"
    :loaded="loaded"
    :error="error"
    @reload="evh_7315092382398562_reload($event, {})"
  >
    <div v-if="loaded && !loading && !stats">
      <ItpAlert show class="col">
        <ItpIcon name="fa-info-circle" class="me-1"> </ItpIcon>
        <span>
          {{
            pgettext(
              'MeterChartsAlert',
              'No data is available for the selected device.'
            )
          }}
        </span>
      </ItpAlert>
    </div>
    <div class="container float-start" v-if="loaded && stats">
      <ItpRow class="mb-4">
        <ItpWidgetCard class="col">
          <template #content>
            <ItpUplot
              :chartData="stats.readings"
              :options="b_5731695935601903_options"
            >
            </ItpUplot>
          </template>
        </ItpWidgetCard>
        <ItpWidgetCard class="col">
          <template #content>
            <ItpUplot
              :chartData="stats.consumptions"
              :options="b_2272469684735879_options"
            >
            </ItpUplot>
          </template>
        </ItpWidgetCard>
      </ItpRow>
      <ItpDataTable
        :value="stats.tableData"
        :editable="b_4186377094414664_editable"
        paging
        tableId="meterAutoReadingStats"
        :columns="b_4186377094414664_columns"
        class="mb-4 shadow-sm"
      >
      </ItpDataTable>
    </div>
  </ItpUsercontrol>
</template>
